import { useCallback, useEffect, useRef, useState } from "react";

import { CoastAlert, CoastAlertSuccess, CoastAlertError, CoastAlertKind } from "./CoastAlert.model";
import { ILog } from "@ihr-radioedit/inferno-core";

const log = ILog.logger("Coast Alerts hook");

/**
 * NOTE: You should always destructure returned values.
 * Otherwise you'll likely get an issue if you pass non-destructured value to one of the useEffect deps
 */
export function useCoastAlerts(initial?: CoastAlert | null) {
  const [alerts, setAlerts] = useState<Record<string, CoastAlert>>({
    ...(initial && { [initial.id]: initial }),
  });
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const updateAlerts = useCallback((cb: (cur: Record<string, CoastAlert>) => Record<string, CoastAlert>) => {
    if (isMounted.current) {
      setAlerts(v => cb(v));
    } else {
      log.debug("Component not mounted");
    }
  }, []);

  const removeAlert = useCallback(
    (id: string) => {
      updateAlerts(cur => {
        const copy = { ...cur };
        delete copy[id];
        return copy;
      });
    },
    [updateAlerts],
  );

  const addAlert = useCallback(
    (alert: CoastAlert) => {
      updateAlerts(cur => ({ ...cur, [alert.id]: alert }));

      if (alert.duration) {
        setTimeout(() => removeAlert(alert.id), alert.duration);
      }

      return alert.id;
    },
    [removeAlert, updateAlerts],
  );

  const clearErrorAlerts = useCallback(() => {
    updateAlerts(cur => {
      const copy: Record<string, CoastAlert> = {};
      Object.entries(cur).forEach(([key, val]) => {
        if (val.kind !== CoastAlertKind.ERROR) {
          copy[key] = val;
        }
      });
      return copy;
    });
  }, [updateAlerts]);

  const clearAllAlerts = useCallback(() => {
    setAlerts({});
  }, []);

  const setMultipleAlerts = useCallback(
    (curAlerts: CoastAlert[]) => {
      updateAlerts(cur => {
        const copy = { ...cur };
        curAlerts.forEach(alert => {
          copy[alert.id] = alert;

          if (alert.duration) {
            setTimeout(() => removeAlert(alert.id), alert.duration);
          }
        });
        return copy;
      });

      return curAlerts.map(n => n.id);
    },
    [removeAlert, updateAlerts],
  );

  const setErrorAlert = useCallback(
    (message?: string, duration?: number) => addAlert(new CoastAlertError(message, duration)),
    [addAlert],
  );

  const setSuccessAlert = useCallback(
    (message?: string, duration?: number) => addAlert(new CoastAlertSuccess(message, duration)),
    [addAlert],
  );

  return {
    alerts,
    setErrorAlert,
    setSuccessAlert,
    setMultipleAlerts,
    removeAlert,
    clearErrorAlerts,
    clearAllAlerts,
  };
}
